import gsap from 'gsap';
import { safeNum } from './dot-cms-aniamtion.helper';
import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';
import { revertMouseEffects } from './common-base-animations';

export const mouseAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts?: gsap.TweenVars) => gsap.core.Tween>
> = {
  'mouse-tilt3d': (s, el) => {
    const htmlEl = el as HTMLElement;
    const angle = safeNum(s.angle, 15);
    const perspective = safeNum(s.perspective, 800);
    const ease = s.easing && s.easing.startsWith('ease') ? 'power1.out' : s.easing ?? 'power1.inOut';
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const invert = s.direction?.toLowerCase().includes('invert');

    htmlEl.style.transformStyle = 'preserve-3d';
    htmlEl.style.transformOrigin = '50% 50%';

    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const dx = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const dy = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      let rotationX = -dy * angle;
      let rotationY = dx * angle;

      if (invert) {
        rotationX = -rotationX;
        rotationY = -rotationY;
      }

      gsap.to(el, {
        rotationX,
        rotationY,
        transformPerspective: perspective,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },

  'mouse-track': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 50);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const ease = s.easing ?? 'power1.inOut';
    const invert = s.direction?.toLowerCase().includes('invert');
    const axis = s.axis ?? 'both';

    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const dx = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const dy = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      const factor = invert ? -1 : 1;

      const x = axis === 'x' || axis === 'both' ? dx * distance * factor : 0;
      const y = axis === 'y' || axis === 'both' ? dy * distance * factor : 0;

      gsap.to(htmlEl, {
        x,
        y,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },

  'mouse-blob': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 30);
    const baseScale = safeNum(s.scale, 1.2);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.28);
    const ease = s.easing ?? 'back.out(1.7)';
    const invert = s.direction === 'invert mouse';
    const axis = s.axis ?? 'both';

    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const normX = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const normY = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);

      const x = axis === 'x' || axis === 'both' ? normX * distance * (invert ? -1 : 1) : 0;
      const y = axis === 'y' || axis === 'both' ? normY * distance * (invert ? -1 : 1) : 0;

      const squishX = axis === 'vertical' ? baseScale : baseScale + Math.abs(x / distance) * 0.35;
      const squishY = axis === 'horizontal' ? baseScale : baseScale + Math.abs(y / distance) * 0.35;

      gsap.to(htmlEl, {
        x,
        y,
        scaleX: squishX,
        scaleY: squishY,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },

  'mouse-obscure': (s, el) => {
    const htmlEl = el as HTMLElement;
    const pointerVelocity = safeNum(s.pointerVelocity, 1);
    const scale = safeNum(s.scale, 1);
    const angle = safeNum(s.angle, 0);
    const blur = safeNum(s.blur, 0);
    const ease = s.easing ?? 'power1.inOut';
    const axis = s.axis ?? 'both';
    const distance = safeNum(s.distance, 30);
    const invert = s.direction === 'invert mouse';

    htmlEl.style.transformOrigin = '50% 50%';
    htmlEl.style.transform = `scale(${scale})`;

    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const normX = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const normY = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      const x = axis === 'x' || axis === 'both' ? normX * distance * (invert ? -1 : 1) : 0;
      const y = axis === 'y' || axis === 'both' ? normY * distance * (invert ? -1 : 1) : 0;

      gsap.to(htmlEl, {
        x,
        y,
        rotation: angle,
        filter: `blur(${blur}px)`,
        ease,
        scale: s.scale,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },

  'mouse-track3d': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 80);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const angle = safeNum(s.angle, 15);
    const perspective = safeNum(s.perspective, 800);
    const ease = s.easing?.startsWith('ease') ? 'power1.out' : s.easing ?? 'power1.inOut';
    const axis = s.axis ?? 'both';

    htmlEl.style.transformStyle = 'preserve-3d';
    htmlEl.style.transformOrigin = '50% 50%';

    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      let dx = e.clientX - (rect.left + rect.width / 2);
      let dy = e.clientY - (rect.top + rect.height / 2);
      const dist = Math.hypot(dx, dy);
      if (dist > distance) {
        const ratio = distance / dist;
        dx *= ratio;
        dy *= ratio;
      }
      const normX = dx / (rect.width / 2);
      const normY = dy / (rect.height / 2);
      const rotationX = axis === 'y' ? 0 : -normY * angle;
      const rotationY = axis === 'x' ? 0 : normX * angle;

      gsap.to(htmlEl, {
        rotationX,
        rotationY,
        transformPerspective: perspective,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },

  'mouse-airy': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 100);
    const pointerVelocity = safeNum(s.pointerVelocity, 1.5);
    const angle = safeNum(s.angle, 0);
    const ease = s.easing ?? 'power1.inOut';
    const axis = s.axis ?? 'both';
    const invert = s.direction === 'invert mouse';

    htmlEl.style.transformOrigin = '50% 50%';

    const handler = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const normX = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const normY = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);

      let x;
      let y;
      let rotate;
      if (s.axis === 'vertical') {
        y = normX * distance * (invert ? -1 : 1);
        rotate = normY * angle * (invert ? -1 : 1);
      } else if (s.axis === 'horizontal') {
        x = normX * distance * (invert ? -1 : 1);
        axis === 'both' ? normX * distance * (invert ? -1 : 1) : 0;
        rotate = normX * angle * (invert ? -1 : 1);
      } else {
        x = normX * distance * (invert ? -1 : 1);
        y = normY * distance * (invert ? -1 : 1);
        rotate = (normX + normY) * (angle / 2);
      }

      gsap.to(el, {
        x,
        y,
        rotate,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },

  'mouse-resize': (s, el) => {
    console.log('resize', s);
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 100);
    const scale = safeNum(s.scale, 1.5);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const ease = s.easing ?? 'power1.inOut';
    const invert = s.direction === 'invert mouse';
    const axis = s.axis ?? 'both';

    htmlEl.style.transformOrigin = '50% 50%';

    const handler = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const normX = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const normY = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);

      let x;
      let y;
      if (s.axis === 'vertical') {
        y = normX * distance * (invert ? -1 : 1);
      } else if (s.axis === 'horizontal') {
        x = normX * distance * (invert ? -1 : 1);
        axis === 'both' ? normX * distance * (invert ? -1 : 1) : 0;
      } else {
        x = normX * distance * (invert ? -1 : 1);
        y = normY * distance * (invert ? -1 : 1);
      }

      gsap.to(el, {
        x,
        y,
        scale,
        ease,
        fontSize: s.size,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },

  'mouse-skew3d': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 100);
    const angle = safeNum(s.angle, 0);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const ease = s.easing ?? 'power1.inOut';
    const invert = s.direction === 'invert mouse';
    const axis = s.axis ?? 'both';
    htmlEl.style.transformOrigin = '50% 50%';
    htmlEl.style.transformStyle = 'preserve-3d';

    const handler = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const normX = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const normY = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);

      let x;
      let y;
      if (s.axis === 'vertical') {
        y = normX * distance * (invert ? -1 : 1);
      } else if (s.axis === 'horizontal') {
        x = normX * distance * (invert ? -1 : 1);
        axis === 'both' ? normX * distance * (invert ? -1 : 1) : 0;
      } else {
        x = normX * distance * (invert ? -1 : 1);
        y = normY * distance * (invert ? -1 : 1);
      }
      gsap.to(htmlEl, {
        x,
        y,
        skewX: normX * angle * (invert ? -1 : 1),
        skewY: normY * angle * (invert ? -1 : 1),
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },
  'mouse-swivel': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 100);
    const angle = safeNum(s.angle, 0);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const ease = s.easing ?? 'power1.inOut';
    const invert = s.direction === 'invert mouse';
    htmlEl.style.transformOrigin = '50% 50%';
    htmlEl.style.transformStyle = 'preserve-3d';

    const handler = (e: MouseEvent) => {
      console.log('swivel', s);
      const rect = el.getBoundingClientRect();
      const normX = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const normY = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);

      let tx = normX * distance * (invert ? -1 : 1);
      let ty = normY * distance * (invert ? -1 : 1);
        origin = 'center';
      if (s.pivot_axis === 'top') {origin = 'top center';}
      else if (s.pivot_axis === 'bottom') {origin = 'bottom center';}
      else if (s.pivot_axis === 'left'){ origin = 'center left';}
      else if (s.pivot_axis === 'right') {origin = 'center right';}

      gsap.to(htmlEl, {
        x: tx,
        y:ty,
        rotation: angle,
        transformOrigin: origin,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    el.parentElement.addEventListener('mousemove', handler);
    const tween = gsap.to(el, { duration: 0 });
    (tween as any).cleanup = () => el.parentElement.removeEventListener('mousemove', handler);
    el.parentElement.addEventListener('mouseleave', () => {revertMouseEffects(el);});
    return tween;
  },
};
